/**
 * Master Data Hooks
 *
 * TanStack Query hooks for master data management (countries, provinces, etc.)
 * Optimized for caching with longer refresh intervals since master data rarely changes
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createVehicleMakeApi,
    createVehicleModelApi,
    createVehicleTrimApi,
    decodeLicenseApi,
    decodeVINApi,
    getCityMasterListApi,
    getCountryMasterListApi,
    getMechanicDetailsApi,
    getProvinceMasterListApi,
    getSelectedServiceCategoryListApi,
    getServiceCategoryListApi,
    getVehicleMakeListApi,
    getVehicleModelListApi,
    getVehicleTrimListApi,
    getVehicleYearListApi,
} from './master.api';

// Query Keys for core master data domain
export const coreMasterKeys = {
    all: ['master'],
    countries: (params) => [...coreMasterKeys.all, 'countries', params],
    cities: (params) => [...coreMasterKeys.all, 'cities', params],
    provinces: (params) => [...coreMasterKeys.all, 'provinces', params],
    vehicleYears: (params) => [...coreMasterKeys.all, 'vehicleYears', params],
    vehicleMakes: (params) => [...coreMasterKeys.all, 'vehicleMakes', params],
    vehicleModels: (params) => [...coreMasterKeys.all, 'vehicleModels', params],
    vehicleTrims: (params) => [...coreMasterKeys.all, 'vehicleTrims', params],
    serviceCategories: (params) => [...coreMasterKeys.all, 'serviceCategories', params],
    selectedServiceCategories: (params) => [...coreMasterKeys.all, 'selectedServiceCategories', params],
    mechanicDetails: () => [...coreMasterKeys.all, 'mechanicDetails'],
};

/**
 * Hook to get country master list
 *
 * Uses optimized caching for country data:
 * - 10-minute cache (rarely changes)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 * @returns {object} Query result with country list data
 */
export const useGetCountryMasterList = (params = {}, options = {}) => {
    return useQuery({
        queryKey: coreMasterKeys.countries(params),
        queryFn: () => getCountryMasterListApi(params),
        ...options,
    });
};

/**
 * Hook to get province master list
 *
 * Uses optimized caching for province data:
 * - 10-minute cache (rarely changes)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 *
 * @param {object} options - Query options
 * @returns {object} Query result with province list data
 */
export const useGetProvinceMasterList = (params = {}, options = {}) => {
    return useQuery({
        queryKey: coreMasterKeys.provinces(params),
        queryFn: () => getProvinceMasterListApi(params),
        ...options,
    });
};

/**
 * Hook to get city master list
 *
 * Uses optimized caching for province data:
 * - 10-minute cache (rarely changes)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 *
 * @param {object} options - Query options
 * @returns {object} Query result with city list data
 */
export const useGetCityMasterList = (params = {}, options = {}) => {
    return useQuery({
        queryKey: coreMasterKeys.cities(params),
        queryFn: () => getCityMasterListApi(params),
        ...options,
    });
};

/**
 * Hook to get vehicle year master list
 *
 * Uses optimized caching for vehicle year data:
 * - 10-minute cache (rarely changes)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 */
export const useGetVehicleYearMasterList = (params = {}, options = {}) => {
    return useQuery({
        queryKey: coreMasterKeys.vehicleYears(params),
        queryFn: () => getVehicleYearListApi(params),
        ...options,
    });
};

/**
 * Hook to get vehicle make master list
 *
 * Uses optimized caching for vehicle make data:
 * - 10-minute cache (rarely changes)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 *
 * @param {object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Number of items per page
 * @param {number} params.year - Vehicle year filter (optional)
 * @param {object} options - Query options
 * @returns {object} Query result with vehicle make list data
 */
export const useGetVehicleMakeList = (params = {}, options = {}) => {
    return useQuery({
        queryKey: coreMasterKeys.vehicleMakes(params),
        queryFn: () => getVehicleMakeListApi(params),
        ...options,
    });
};

/**
 * Hook to get service category master list
 *
 * Uses optimized caching for service category data:
 * - 10-minute cache (rarely changes)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 *
 * @param {object} params - Query parameters
 * @param {number} params.limit - Number of items per page
 * @param {object} options - Query options
 * @returns {object} Query result with service category list data
 */
export const useGetVehicleModelList = (params = {}, options = {}) => {
    return useQuery({
        queryKey: coreMasterKeys.vehicleModels(params),
        queryFn: () => getVehicleModelListApi(params),
        ...options,
    });
};
export const useGetVehicleTrimList = (params = {}, options = {}) => {
    return useQuery({
        queryKey: coreMasterKeys.vehicleTrims(params),
        queryFn: () => getVehicleTrimListApi(params),
        ...options,
    });
};
export const useGetServiceCategoryList = (params = {}, options = {}) => {
    return useQuery({
        queryKey: coreMasterKeys.serviceCategories(params),
        queryFn: () => getServiceCategoryListApi(params),
        ...options,
    });
};

export const useGetSelctedServiceCategoryList = (params = {}, options = {}) => {
    return useQuery({
        queryKey: coreMasterKeys.selectedServiceCategories(params),
        queryFn: () => getSelectedServiceCategoryListApi(params),
        ...options,
    });
};

/**
 * Hook to create vehicle make
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with createMake function and loading states
 */
export const useCreateVehicleMake = (options = {}) => {
    return useMutation({
        mutationFn: createVehicleMakeApi,
        ...options,
    });
};

/**
 * Hook to create vehicle model
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with createModel function and loading states
 */
export const useCreateVehicleModel = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createVehicleModelApi,
        ...options,
    });
};

/**
 * Hook to create vehicle trim
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with createTrim function and loading states
 */
export const useCreateVehicleTrim = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createVehicleTrimApi,
        ...options,
    });
};

/**
 * Hook to decode VIN
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with decodeVIN function and loading states
 */
export const useDecodeVIN = (options = {}) => {
    return useMutation({
        mutationFn: decodeVINApi,
        ...options,
    });
};

/**
 * Hook to decode license plate
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with decodeLicense function and loading states
 */
export const useDecodeLicense = (options = {}) => {
    return useMutation({
        mutationFn: decodeLicenseApi,
        ...options,
    });
};

/**
 * Hook to get mechanic/assignee details list
 *
 * Uses optimized caching for mechanic data:
 * - 5-minute cache (may change when mechanics are added/updated)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 *
 * @param {object} options - Query options
 * @returns {object} Query result with mechanic list data
 */
export const useGetMechanicDetails = (options = {}) => {
    return useQuery({
        queryKey: coreMasterKeys.mechanicDetails(),
        queryFn: () => getMechanicDetailsApi(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        ...options,
    });
};
